import React from 'react';

import PropTypes from 'prop-types';

import hotkeys from 'hotkeys-js';

import style from '../ChartStyle.jsx';
import { ThresholdsPopup, THR_TRANSLATION_KEYS } from './ThresholdsPopup.jsx';

import { READ, WRITE } from '../Om7Sense';

// These include one "unit" of time that the large HHLL needs
const intervalToTimeFrame = {
    '4h': 5 * 60 * 60,
    '3d': 3 * 24 * 60 * 60 + (60 * 60),
    '1w': 8 * 24 * 60 * 60,
    '1m': 32 * 24 * 60 * 60,
    '1y': 372 * 24 * 60 * 60
};

const intervalToResolution = {
    '4h': 10 * 1000,
    '3d': 10 * 60 * 1000,
    '1w': BG.charts.ESpans.HOUR,
    '1m': BG.charts.ESpans.HOUR,
    '1y': BG.charts.ESpans.DAY
};

var intervalToHhllNames;

const indicatorColors = {
    "hhllSmall": "blue",
    "hhllMedium": "green"
};
var indicatorColorsEnglish;

const DEBUG = false;


export default class ChartPopup extends ThresholdsPopup {
    static contextTypes = {
        t: PropTypes.func.isRequired,
        p: PropTypes.object.isRequired,
        logged_in_as: PropTypes.string,
        basePath: PropTypes.string
    }

    controller = null;

    // Horizontal lines showing the thresholds
    lines = [];
    linesInitialized = false;

    // When we're updating a handle because a user entered a number, we don't want overwrite
    // this number in the state with the callback from the handle move caller.
    ignoreHandleUpdateCallbacks = {};

    constructor(props, context) {
        super();
        const t = context.t;
        intervalToHhllNames = {
            '4h': {
                'hhllSmall': t('chart.hhll_10s'),
                'hhllMedium': t('chart.hhll_10m')
            },
            '3d': {
                'hhllSmall': t('chart.hhll_10m'),
                'hhllMedium': t('chart.hhll_1h')
            },
            '1w': {
                'hhllSmall': t('chart.hhll_1h'),
                'hhllMedium': t('chart.hhll_1d')
            },
            '1m': {
                'hhllSmall': t('chart.hhll_1h'),
                'hhllMedium': t('chart.hhll_1d')
            },
            '1y': {
                'hhllSmall': t('chart.hhll_1d'),
                'hhllMedium': t('chart.hhll_1w')
            }
        };
        indicatorColorsEnglish = {
            "hhllSmall": t('chart.blue'),
            "hhllMedium": t('chart.green')
        }
        let newState = {
            data: null,
            name: null,
            interval: "4h",
            hhllTitles: intervalToHhllNames['4h'],
            indicators: [
                { id: 'hhllSmall', selected: false },
                { id: 'hhllMedium', selected: false }
            ],
            modalHeight: 1024
        };

        const name = this.getName(props);

        if (name) {
            newState.name = name;
        }
        this.state = newState;

        this.doClose = this.doClose.bind(this);
        this.setInterval = this.setInterval.bind(this);

        this.modalContainerRef = React.createRef();
    }

    updateInDS = (data, oldValue, relativePath, varName, path) => {
        this.setState((state) => {
            let oldData = state.data ? state.data : {};

            // A data update can push just a TS extension
            if (data.tsLast) {
                oldData.ts = data.tsLast;
            } else if (data.ts) {
                // Or a whole tick
                oldData.ts = data.ts;
            }
            if (data.v) {
                oldData.v = data.v;
            }
            const update = { data: oldData };
            if (data.v !== undefined) {
                update.v = data.v;
            }
            if (data.type !== undefined) {
                update.type = data.type;
            }
            if (data.unit !== undefined) {
                update.unit = data.unit;
            }            

            return update;
        });
    }

    connectChart() {
        if (this.context.logged_in_as && !this.timeSeries) {
            const self = this;

            const path = this.getPathParam(this.props);
            const name = this.getName(this.props);

            if (path) {
                this.context.p.subscribe(path, this.updateInDS, true).then((sub) => { self.sub = sub; });
            }
            if (DEBUG) {
                console.log("ChartPopup mounted");
            }

            hotkeys('esc', self.doClose.bind(self, false));

            let controller = new BG.charts.Controller({
                'id': 'charting',
                'interval': BG.charts.ESpans.TICK
                /* additional properties can be specified here */
            });

            controller.set("type", "line");
            if (this.modalHeight) {
                controller.set('height', this.modalHeight);
            }
            controller.setStyle(style);

            this.controller = controller;

            this.applyControllerState(controller);

            // add some data
            this.timeSeries = controller.addTimeSeries('OmSense(' + path + ',' + this.currentTimeFrame + ',' + encodeURIComponent(name) + ',false)'); // ,' + this.state.interval * 1000 + ',7200
            // controller.addIndicator('Volume');

            // controller.addIndicator('EMA(50,20)');

            // controller.addTimeSeries(„Expression(Identifier:Arg1:Arg2 + Identifier:Arg2:Arg3)“)

            this.updateLines();

            return true;
        }
    }

    updateLines() {
        if (this.controller && this.state.thr && !this.linesInitialized) {
            if (this.state.rule) {
                for (let key in this.state.thr) {
                    const val = this.state.thr[key];
                    const isDisabled = key.length > 2;
                    const valName = isDisabled ? key.substring(0, 2) : key;
                    this.updateTool(this.controller, valName, !isDisabled, val);
                }
            }
            this.linesInitialized = true;
        }
    }

    UNSAFE_componentWillReceiveProps (nextProps, nextContext) {
        if (DEBUG) { console.log("cwrp", nextContext); }
        this.connectChart();
    }

    componentDidMount () {
        if (DEBUG) { 
            console.log("componentDidMount", this.context);
        }
        this.updateDimensionsFn = this.updateDimensions.bind(this);
        window.addEventListener("resize", this.updateDimensionsFn);

        this.updateDimensions();
        this.connectChart();
    }

    isIndicatorEnabled(name) {
        for (let i in this.state.indicators) {
            if (this.state.indicators[i].id === name) {
                return this.state.indicators[i].selected;
            }
        }
    }

    applyControllerState(controller) {
        if (!controller) {
            controller = this.controller;
        }
        if (!controller) {
            return;
        }

        const wantsShowHhllMedium = this.isIndicatorEnabled('hhllMedium'), isShowingHhllMedium = (this.hhllLarge !== undefined && this.hhllLarge !== null);
        const wantsShowHhllSmall = this.isIndicatorEnabled('hhllSmall'), isShowingHhllSmall = (this.hhllSmall !== undefined && this.hhllSmall !== null);

        if (wantsShowHhllSmall !== isShowingHhllSmall) {
            if (!isShowingHhllSmall && wantsShowHhllSmall) {
                this.hhllSmall = controller.addIndicator('HHLL(1)');
                this.hhllSmall.set('customName', this.state.hhllTitles.hhllSmall);
                const hhllShortRef = controller.getObject({objType:"layer"}, this.hhllSmall, 0);
                hhllShortRef.set('fillColor', 'rgba(0, 0, 200, 0.09)');
                // hhllShortRef.set('lineColor', 'rgba(230, 30, 60, 0.2)');
            } else {
                controller.removeObject(this.hhllSmall);
                this.hhllSmall = null;
            }
        }

        if (wantsShowHhllMedium !== isShowingHhllMedium || intervalToResolution[this.state.interval] != controller.get('interval')) {
            if (this.hhllLarge) {
                controller.removeObject(this.hhllLarge);
                this.hhllLarge = null;
            }
            if (wantsShowHhllMedium) {
                if (this.state.interval === '4h') {
                    this.hhllLarge = controller.addIndicator('HHLL(60)');
                } else if (this.state.interval === '3d') {
                    this.hhllLarge = controller.addIndicator('HHLL(12)');
                } else if (this.state.interval === '1w' || this.state.interval === '1m') {
                    this.hhllLarge = controller.addIndicator('HHLL(24)');
                } else if (this.state.interval === '1y') {
                    this.hhllLarge = controller.addIndicator('HHLL(7)');
                }
                const hhllMediumRef = controller.getObject({objType:"layer"}, this.hhllLarge, 0);
                hhllMediumRef.set('fillColor', 'rgba(0, 200, 0, 0.09)');
                this.hhllLarge.set('customName', this.state.hhllTitles.hhllMedium);
            }
        }

        if (intervalToResolution[this.state.interval] != controller.get('interval') || this.currentTimeFrame != intervalToTimeFrame[this.state.interval]) {
            let smallHhLlTitle = null;
           
            switch (this.state.interval) {
                case "4h": {
                    controller.set('span', 'i');
                    break;
                }
                case "3d": {
                    controller.set('span',  '3 days');
                    break;
                }
                case "1w": {
                    controller.set('span',  '1 week');
                    break;
                }
                case "1m": {
                    controller.set('span', '1 month');
                    break;
                }
                case "1y": {
                    controller.set('span', '1 year');
                    break;
                }
            }
            smallHhLlTitle = this.state.hhllTitles.hhllSmall;
            controller.set('interval', intervalToResolution[this.state.interval]);

            // Check if we have loaded enough data to display for the current interval
            const newTimeFrame = intervalToTimeFrame[this.state.interval];
            if (newTimeFrame > this.currentTimeFrame && this.timeSeries) {
                // Replace the curren timeseries with the new one
                this.timeSeries = controller.replaceTimeSeries('OmSense(' + this.getPathParam(this.props) + ',' + newTimeFrame + ',' + encodeURIComponent(this.state.name) + ',false)');
            }
            this.currentTimeFrame = newTimeFrame;

            if (this.hhllSmall) {
                this.hhllSmall.set('customName', smallHhLlTitle);
            }
            return smallHhLlTitle;
        }
    }

    updateDimensions = (e) => {
        if (this.modalContainerRef.current) {

            this.setState((state) => {
                const height = this.modalContainerRef.current.clientHeight;

                const update = {};

                // Check if the window got bigger - since we cannot rely on the modal container size anymore once we
                // make the window bigger since it will shrink to fit it's contents
                if (state.windowHeight === 0) {
                    // We don't know anything about the old size, just set the current size
                    return {modalHeight: height, windowHeight: window.innerHeight};
                }
                // Check if window got bigger
                const windowHeightChange = (window.innerHeight - state.windowHeight) / window.devicePixelRatio;
                if (windowHeightChange > 0) {
                    console.log("windowHeightChange", windowHeightChange, "old modal", state.modalHeight, state.modalHeight + windowHeightChange);
                    // Yes, add the height increase to the last modal height
                    return {modalHeight: state.modalHeight + windowHeightChange, windowHeight: window.innerHeight};
                } else {
                    console.log("windowHeightChange", windowHeightChange, "old modal", state.modalHeight, height);
                    return {modalHeight: height, windowHeight: window.innerHeight};
                }
            });

            this.modalHeight = this.modalContainerRef.current.clientHeight;
            if (this.controller) {
                this.controller.set('height', this.modalHeight);
            }
        }
    }

    componentWillUnmount () {
        if (this.controller) {
            try {
                this.controller.destroy();
            } catch (e) { console.warn("Cannot destroy controller", e); }

            this.setState({'controller': null});
        }

        window.removeEventListener("resize", this.updateDimensionsFn);

        super.componentWillUnmount();

        if (this.sub) {
            this.sub();
            this.sub = null;
        }
    }

    setMeta () {
        super.setMeta();
        this.updateLines();
    }

    setInterval (event) {
        this.setState({interval: event.target.value, 'hhllTitles': intervalToHhllNames[event.target.value]});
    }

    __changeSelection (id) {
        var state = this.state.indicators.map(function(d) {
            return {
                id: d.id,
                selected: (d.id === id ? !d.selected : d.selected)
            };
        });

        this.setState({ indicators: state });
    }

    updateTool(controller, valName, isEnabled, start) {
        const self = this;

        let tool = this.lines[valName];
        if (tool === undefined) {
            // Create a new line
            const handles = [
                [+this.state.data.ts, parseFloat(start)] // , [this.state.data.ts, 1000]
            ];

            if (DEBUG) { 
                console.log("handles for creation", handles);
            }
            tool = controller.addTool("HorizontalLine", handles, controller.getObject({objType: 'chart'}));
            tool.set('descriptionTextColor', 'rgb(0, 0, 0)');

            // Get updates when the handle has been dragged
            tool.addListener('attrChanged', () => {
                if (self.ignoreHandleUpdateCallbacks[valName]) {
                    return;
                }
                const newValue = parseFloat(tool.get('value')).toFixed(2);
                // console.log("new y", newValue, typeof newValue);
                self.setThresholdValue(valName, newValue);
            });
            this.lines[valName] = tool;

        }

        // For disabled lines set half transparent
        const isWarning = valName.charAt(0) === 'w';
        let color;
        if (isWarning) {
            color = isEnabled ? 'rgba(255, 200, 3, 0.7)' : 'rgba(255, 200, 3, 0.35)'
        } else {
            color = isEnabled ? 'rgba(255, 83, 73, 0.6)' : 'rgba(255, 83, 73, 0.3)'
        }

        if (isEnabled) {
            BG.charts.utils.rmFlag(tool, BG.charts.Object.EFlags.LOCKED);
        } else {
            BG.charts.utils.addFlag(tool, BG.charts.Object.EFlags.LOCKED);
        }

        tool.set('description', this.context.t("thresholds." + THR_TRANSLATION_KEYS[valName]));
        tool.set('descriptionFillColor', color);
        tool.set('lineStyle', isEnabled ? 0 : 1);
        tool.set('lineColor', color);
    }

    setToolHandle(controller, valName, value) {
        let tool = this.lines[valName];
        if (!tool || !this.state.data.ts || isNaN(this.state.data.ts)) {
            return;
        }

        // console.log("moveHandle", +this.state.data.ts, value);
        tool.moveHandle(0, +this.state.data.ts, value);
    }

    onThresholdEnabledChanged (valName, keyAdded, keyRemoved, wasEnable, value) {
        if (this.controller) {
            this.updateTool(this.controller, valName, wasEnable, value);
        }
    }

    setThreshold = (valName, ev) => {
        this.ignoreHandleUpdateCallbacks[valName] = true;
        let newValue = ev.target.value;
        this.setThresholdValue(valName, newValue);

        if (this.controller) {
            this.setToolHandle(this.controller, valName, newValue);
        }
        this.ignoreHandleUpdateCallbacks[valName] = false;
    }

    render () {
        const self = this, t = this.context.t;
        let thresholdsEl = null;
        let hasChange = false;
        if (this.state.thr) {
            thresholdsEl = this.renderThresholds();
            let data = this.state.thr;
            hasChange = this.state.rule && !this.state.locked && this.origThr && (this.origThr.wa != data.wa || this.origThr.wb != data.wb || this.origThr.ca != data.ca || this.origThr.cb != data.cb || this.origThr.name != this.state.name);
        }

        this.applyControllerState();


        let indicators = this.state.indicators.map(function(d) {
            const bandColor = indicatorColorsEnglish[d.id];
            const bandColorId = indicatorColors[d.id];
            return (
                <tr key={d.id}>
                    <td><input type="checkbox" checked={d.selected} onChange={this.__changeSelection.bind(this, d.id)} /></td>
                    <td>{self.state.hhllTitles[d.id]}</td>
                    <td><div className={"color " + bandColorId}>{bandColor}</div></td>
                </tr>
            );
        }.bind(this));



        let minHeight = 500;

        if (document.body.clientHeight - 180 < 500) {
            minHeight = (document.body.clientHeight - 180); 
        }
        
        // <a onClick={self.doClose} ariaLabel="Close" className="modal-overlay"><i className="fa fa-close" /></a>
        return (
            <div className="modal active">
                <div className="modal-container" style={{}} ref={this.modalContainerRef}>
                    <a onClick={self.doClose.bind(self, hasChange)} className="btn btn-clear float-right" aria-label="Close"></a>
                    <div className="columns">
                        <div className="column col-4 modal-body" style={{height: this.state.modalHeight}}>
                            <div className="modal-header">
                                
                                <div className="modal-title h5">{this.state.name}</div>
                            
                                <div className="chart-timeframe">
                                    <label htmlFor="interval">{t('chart.interval')}: 
                                        <select name="interval" onChange={this.setInterval} value={this.state.interval}>
                                            <option value="4h">{t('chart.interval_4h')}</option>
                                            <option value="3d">{t('chart.interval_3d')}</option>
                                            <option value="1w">{t('chart.interval_1w')}</option>
                                            <option value="1m">{t('chart.interval_1m')}</option>
                                            <option value="1y">{t('chart.interval_1y')}</option>
                                        </select>
                                    </label>
                                </div>
                                
                                <table cellSpacing={0} cellPadding={0}>
                                    <thead>
                                        <tr>
                                            <th>{t('chart.header_activated')}</th>
                                            <th>{t('chart.header_additional_value')}</th>
                                            <th>{t('chart.header_band_color')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {indicators}
                                    </tbody>
                                </table>
                                
                                <div>
                                    { thresholdsEl }
                                </div>
                                
                                
                            </div>
                        </div>
                        <div className="col-8 column chart-main" id="charting" ref="chartMain" style={{"height":this.state.modalHeight}}></div>
                        
                    </div>
                </div>
                
                    
            </div>
        );
    }
};

